'use client';

import { useTranslation } from '@/hooks/useTranslation';
import Image from 'next/image';
import LouiseButton, { CtaTargetType } from '@/components/buttons/LouiseButton';
import ComparisonSummary from '@/components/blocks/ComparisonSummary';
import CTABanner from '@/components/blocks/CTABanner';
import React from 'react';

interface PageContentProps {
  locale: string;
}

export default function PageContent({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);

  return (
    <div>
      <div className="why-video tw-flex tw-flex-col md:tw-p-0 tw-p-5">
        <section className="tw-flex tw-items-center tw-justify-center tw-flex-col tw-mt-8">
          <article className="tw-flex-col tw-justify-start tw-items-start tw-gap-5 tw-flex tw-my-8">
            <h1 className="md:tw-w-[780px] tw-text-gray-700 tw-text-4xl tw-font-semibold tw-font-['Lexend'] tw-leading-10">
              {t('CompareTextAndVideo/title')}
            </h1>
            <Image
              priority
              quality={100}
              className="md:tw-w-[780px] tw-w-full md:tw-h-[440px] tw-h-full tw-bg-blue-900 tw-rounded-sm tw-object-cover"
              src="https://cdn.1cdn.app/application/LOUISEBOT/24081711380352_Why_Choose_a_Video_Chatbot_Over_a_Text-Based_Chatbot.png"
              alt={t('CompareTextAndVideo/title')}
              title={t('CompareTextAndVideo/title')}
              width={934}
              height={513}
            />
            <p className="md:tw-w-[780px] tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
              {t('CompareTextAndVideo/introduction')}
            </p>
          </article>

          <LouiseButton
            color="blue"
            cta={t('CompareTextAndVideo/engagement/btn')}
            ctaTarget="register"
            locale={locale as CtaTargetType}
          />

          <article className="tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex tw-my-8">
            <h2 className="md:tw-w-[780px] tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
              {t('CompareTextAndVideo/engagement/title')}
            </h2>
            <div className="md:tw-w-[780px] tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
              <p>{t('CompareTextAndVideo/engagement/description')}</p>
              <ul className="tw-list-disc tw-list-inside">
                <li>{t('CompareTextAndVideo/engagement/l-01')}</li>
                <li>{t('CompareTextAndVideo/engagement/l-02')}</li>
                <li>{t('CompareTextAndVideo/engagement/l-03')}</li>
                <li>{t('CompareTextAndVideo/engagement/l-04')}</li>
                <li>{t('CompareTextAndVideo/engagement/l-05')}</li>
              </ul>
            </div>
          </article>

          <LouiseButton
            color="blue"
            cta={t('CompareTextAndVideo/interactive/btn')}
            ctaTarget="register"
            locale={locale}
          />
        </section>

        <section className="tw-flex-col tw-justify-center tw-items-center tw-gap-8 tw-flex">
          <Image
            priority
            quality={100}
            className="md:tw-w-[780px] tw-w-full md:tw-h-[428px] tw-h-full tw-rounded-sm tw-mt-8"
            src="https://cdn.1cdn.app/application/LOUISEBOT/24081711380354_Interactive_Video_Chatbots,_A_GameChanger.png"
            alt={t('CompareTextAndVideo/interactive/title')}
            title={t('CompareTextAndVideo/interactive/title')}
            width={934}
            height={513}
          />
          <article className="tw-flex-col tw-justify-center tw-items-center tw-gap-3 tw-flex tw-my-8">
            <h2 className="md:tw-w-[780px] tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
              {t('CompareTextAndVideo/interactive/title')}
            </h2>
            <div className="md:tw-w-[780px] tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal tw-mb-2">
              <p>{t('CompareTextAndVideo/interactive/description')}</p>
              <ul className="tw-list-disc tw-list-inside">
                <li>{t('CompareTextAndVideo/interactive/l-01')}</li>
                <li>{t('CompareTextAndVideo/interactive/l-02')}</li>
                <li>{t('CompareTextAndVideo/interactive/l-03')}</li>
                <li>{t('CompareTextAndVideo/interactive/l-04')}</li>
                <li>{t('CompareTextAndVideo/interactive/l-05')}</li>
              </ul>
            </div>

            <LouiseButton color="blue" cta={t('CompareTextAndVideo/battle/btn')} ctaTarget="register" locale={locale} />
          </article>

          <Image
            priority
            quality={100}
            className="md:tw-w-[780px] tw-w-full md:tw-h-[428px] tw-h-full tw-rounded-sm"
            src="https://cdn.1cdn.app/application/LOUISEBOT/24070311330863_3.jpg"
            alt={t('CompareTextAndVideo/battle/title')}
            title={t('CompareTextAndVideo/battle/title')}
            width={780}
            height={428}
          />
          <article className="tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
            <h2 className="md:tw-w-[780px] tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
              {t('CompareTextAndVideo/battle/title')}
            </h2>
            <div className="md:tw-w-[780px]">
              <p className="tw-text-gray-700 tw-text-base tw-font-bold tw-font-['Lexend'] tw-leading-normal">
                {t('CompareTextAndVideo/battle/t-01')}
              </p>
              <p className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                {t('CompareTextAndVideo/battle/l-01')}
              </p>
              <p className="tw-text-gray-700 tw-text-base tw-font-bold tw-font-['Lexend'] tw-leading-normal">
                {t('CompareTextAndVideo/battle/t-02')}
              </p>
              <p className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                {t('CompareTextAndVideo/battle/l-02')}
              </p>
              <p className="tw-text-gray-700 tw-text-base tw-font-bold tw-font-['Lexend'] tw-leading-normal">
                {t('CompareTextAndVideo/battle/t-03')}
              </p>
              <p className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                {t('CompareTextAndVideo/battle/l-03')}
              </p>
              <p className="tw-text-gray-700 tw-text-base tw-font-bold tw-font-['Lexend'] tw-leading-normal">
                {t('CompareTextAndVideo/battle/t-04')}
              </p>
              <p className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                {t('CompareTextAndVideo/battle/l-04')}
              </p>
              <p className="tw-text-gray-700 tw-text-base tw-font-bold tw-font-['Lexend'] tw-leading-normal">
                {t('CompareTextAndVideo/battle/t-05')}
              </p>
              <p className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
                {t('CompareTextAndVideo/battle/l-05')}
              </p>
            </div>
          </article>
          <LouiseButton color="blue" cta={t('CompareTextAndVideo/choose/btn')} ctaTarget="register" locale={locale} />

          <Image
            priority
            quality={100}
            className="md:tw-w-[780px] tw-w-full md:tw-h-[428px] tw-h-full tw-rounded-sm"
            src="https://cdn.1cdn.app/application/LOUISEBOT/24081711380432_Why_Choose_Louisebot_for_Your_Video_Chatbot_Needs.png"
            alt={t('CompareTextAndVideo/choose/title')}
            title={t('CompareTextAndVideo/choose/title')}
            width={932}
            height={513}
          />
          <article className="tw-flex-col tw-justify-center tw-items-center tw-gap-3 tw-flex">
            <h2 className="md:tw-w-[780px] tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
              {t('CompareTextAndVideo/choose/title')}
            </h2>
            <p className="md:tw-w-[780px] tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
              {t('CompareTextAndVideo/choose/description')}
            </p>
          </article>
          <p className="md:tw-w-[780px] tw-text-gray-700 tw-text-xl tw-font-normal tw-font-['Lexend'] tw-leading-7"></p>

          <ComparisonSummary summery={t('CompareTextAndVideo/choose/conclusion')} locale={locale} />
        </section>

        <CTABanner
          title={t('Home/ReadyToGetStarted/cta-title')}
          description={t('Home/ReadyToGetStarted/cta-description')}
          cta={t('Home/ReadyToGetStarted/start-trial-aria')}
          ctaTarget="register"
          locale={locale}
        />
      </div>
    </div>
  );
}
